import { createSelector } from '@reduxjs/toolkit';
import { USER_CONSTANTS } from '../constants';
import { DEVICE_CONSTANTS, DeviceStatus } from '../constants/deviceConstants';
import { BeaconUUID } from '../slices/beacons';
import { Cluster, selectClustersById } from '../slices/clusters';
import { selectSearchFilter, selectSelectedClusters } from '../slices/ui';
import { type User, selectCurrentUser, selectIsImpersonating, selectUsersByID, selectUserToken } from '../slices/users';
import { clusterContains } from '../utils';
import { userContains } from '../utils/user';
import { selectVisibleDriverBeacons } from './beacons';

type BeaconLike = ReturnType<typeof selectVisibleDriverBeacons>[number];

type EnrichedDriver = User & {
  currentBeaconUuid?: BeaconUUID;
  beacon?: BeaconLike;
  beaconID?: BeaconUUID;
  readableBeaconID?: string;
  hidden: boolean;
  status: DeviceStatus;
  activeSession?: BeaconLike['activeSession'];
  cluster?: Cluster & { role: string };
  latitude?: number;
  longitude?: number;
  locationDate?: string;
};

const VISIBLE_ROLES = [
  USER_CONSTANTS.ROLE.ADMIN,
  USER_CONSTANTS.ROLE.IMPLEMENT_MANAGER,
  USER_CONSTANTS.ROLE.DRIVER,
  USER_CONSTANTS.ROLE.SEASONAL_DRIVER,
] as const;

export const selectUsersAsArray = createSelector([selectUsersByID], (usersByID) =>
  Object.values(usersByID).filter((user) => (user.cluster_roles || []).some((cr) => VISIBLE_ROLES.includes(cr.role))),
);

export const selectIsUserAdminByClusterId = createSelector([selectCurrentUser], (user) =>
  Object.fromEntries((user.cluster_roles || []).map((cr) => [cr.cluster_id, cr.role === USER_CONSTANTS.ROLE.ADMIN])),
);

export const selectUserUnits = createSelector([selectCurrentUser], (user) => ({
  area: user.unit_area ?? USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI,
  distance: user.unit_distance ?? USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI,
  time: user.unit_time ?? USER_CONSTANTS.UNITS.TIME.STANDARD,
}));

const selectDrivers = createSelector([selectUsersAsArray], (users) =>
  users.filter((user) =>
    (user.cluster_roles || []).some(
      (cr) => cr.role === USER_CONSTANTS.ROLE.DRIVER || cr.role === USER_CONSTANTS.ROLE.SEASONAL_DRIVER,
    ),
  ),
);

const getDriversSelector = ({ withAdmins } = { withAdmins: false }) =>
  createSelector(
    [
      withAdmins ? selectUsersAsArray : selectDrivers,
      selectVisibleDriverBeacons,
      selectClustersById,
      selectSelectedClusters,
      selectSearchFilter,
    ],
    (drivers, beacons, clusterById, activeClusters, filter) => {
      const driversByID: Record<number, EnrichedDriver> = {};
      const idToFilter: number[] = [];
      drivers.forEach((d) => {
        let beacon: BeaconLike | undefined,
          beaconID: string | undefined,
          readableBeaconID: string | undefined,
          activeSession: BeaconLike['activeSession'] | undefined,
          latitude: number | undefined,
          longitude: number | undefined,
          locationDate: string | undefined;
        let status: DeviceStatus = DEVICE_CONSTANTS.STATUS.AVAILABLE;
        beacons.forEach((b) => {
          if (b.currentUserId === d.id) {
            beacon = b;
            beaconID = b.uuid;
            readableBeaconID = b.readableUuid;
            status = b.status;
            activeSession = b.activeSession;
            latitude = b.locationLatitude;
            longitude = b.locationLongitude;
            locationDate = b.locationDate;
          }
        });
        let isDriverClusterCompliant = !activeClusters.length;
        (d.cluster_roles || []).forEach((r) => {
          if (activeClusters.includes(r.cluster_id)) {
            isDriverClusterCompliant = true;
          }
          const storedCluster = clusterById[r.cluster_id];
          const cluster = storedCluster ? { ...storedCluster, role: r.role } : undefined;
          const currentBeaconUuid = d.current_beacon_uuid;
          driversByID[d.id] = {
            ...d,
            currentBeaconUuid,
            beacon,
            beaconID,
            readableBeaconID,
            hidden: !userContains(d, filter) && !clusterContains(cluster, filter),
            status,
            activeSession,
            cluster,
            latitude,
            longitude,
            locationDate,
          };
        });
        if (!isDriverClusterCompliant) {
          idToFilter.push(d.id);
        }
      });
      idToFilter.forEach((driverID) => delete driversByID[driverID]);
      return driversByID;
    },
  );

const selectDriversWithCurrentBeacon = getDriversSelector();

export const selectDriversWithCurrentBeaconAsArray = createSelector([selectDriversWithCurrentBeacon], (driversByID) =>
  Object.values(driversByID),
);

export const selectDriversWithCurrentBeaconWithAdmins = getDriversSelector({ withAdmins: true });

export const selectDriversWithCurrentBeaconWithAdminsAsArray = createSelector(
  [selectDriversWithCurrentBeaconWithAdmins],
  (driversByID) => Object.values(driversByID),
);

export const selectImpersonationToken = createSelector(
  [selectIsImpersonating, selectUserToken],
  (isImpersonating, token) => (isImpersonating ? token : undefined),
);
